import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FaUserGroup, FaArrowRight } from "react-icons/fa6";
import PageHero from "../components/PageHero";

const COURSES = [
  {
    icon: <FaUserGroup />,
    track: "Family & Public",
    title: "Generative AI for Parents",
    desc: "A practical, jargon-free course for parents who want to understand generative AI — what it is, how it shows up in their children's lives, and how to guide healthy use at home and in school.",
    points: ["What AI actually is (and isn't)", "AI in classrooms and homework", "Healthy boundaries and habits"],
    accent: "bg-brand-deep text-bone",
  },
];

export default function Courses() {
  useEffect(() => {
    document.title = 'Courses — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);
  return (
    <div className="w-full">
      <PageHero
        eyebrow="Tailored Courses"
        title={
          <>
            Specialized courses for{" "}
            <span className="italic text-brand-deep">modern learners,</span>{" "}
            educators, and families.
          </>
        }
        lead="Practical, applied programs designed to feel relevant and accessible. Our first course — Generative AI for Parents — is open for interest now, with additional courses currently in development."
      />

      {/* Course list — staggered editorial cards */}
      <section className="bg-bone">
        <div className="container-page py-16 lg:py-24 space-y-10">
          {COURSES.map((c) => (
            <article
              key={c.title}
              className={`${c.accent} rounded-3xl p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start`}
            >
              <div className="lg:col-span-3 flex lg:flex-col items-center lg:items-start gap-4">
                <div className="text-3xl">{c.icon}</div>
                <div>
                  <div className="text-[11px] tracking-[0.18em] uppercase opacity-70">
                    {c.track}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-6">
                <h2 className="font-display text-3xl leading-tight">{c.title}</h2>
                <p className="mt-3 leading-relaxed opacity-90">{c.desc}</p>
              </div>
              <div className="lg:col-span-3">
                <ul className="space-y-2 text-sm">
                  {c.points.map((p) => (
                    <li key={p} className="flex items-start gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                      <span className="opacity-90">{p}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to="/courses/ai-for-parents"
                  className="mt-5 inline-flex items-center gap-2 border-b border-current pb-1 text-sm font-medium hover:opacity-80"
                >
                  View course details <FaArrowRight className="text-[10px]" />
                </Link>
              </div>
            </article>
          ))}

          {/* More in development */}
          <article className="rounded-3xl border border-dashed border-ink/20 bg-cream/60 p-8 lg:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-3">
              <div className="text-[11px] tracking-[0.18em] uppercase text-brand-deep">
                More courses
              </div>
              <div className="font-display italic text-2xl text-ink/40 mt-1">
                in development
              </div>
            </div>
            <div className="lg:col-span-6">
              <h2 className="font-display text-2xl lg:text-3xl leading-tight text-ink">
                Additional courses are on the way.
              </h2>
              <p className="mt-3 text-slate2 leading-relaxed">
                We are actively developing new courses across research skills,
                educator professional development, and academic and career
                advancement. Tell us what would be most useful for your context and
                we will keep you posted as new courses open.
              </p>
            </div>
            <div className="lg:col-span-3 lg:text-right">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border-b border-ink pb-1 text-sm font-medium text-ink hover:opacity-80"
              >
                Suggest a course <FaArrowRight className="text-[10px]" />
              </Link>
            </div>
          </article>
        </div>
      </section>

      {/* Custom programs band */}
      <section className="bg-cream">
        <div className="container-page py-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow eyebrow-bar">Custom Cohorts</div>
            <h2 className="mt-5 font-display text-3xl lg:text-4xl leading-tight tracking-tightish text-ink">
              Want a course shaped around your team, school, or department?
            </h2>
            <p className="mt-4 text-slate2 leading-relaxed">
              We design custom cohorts and on-site sessions that adapt our courses to
              your context — whether that is a parent community, a research lab, or a
              faculty group preparing for a new initiative.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link to="/contact" className="btn-primary">
              Request a custom cohort <FaArrowRight className="text-xs" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
